
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Heart, MessageSquare, Clock, Pin, Lock, Eye, Bell, BellOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from '@/hooks/useSEO';

interface TopicWithDetails {
  id: string;
  title: string;
  is_pinned: boolean;
  is_locked: boolean;
  posts_count: number;
  views_count: number;
  created_at: string;
  last_posted_at: string;
  forum_categories: {
    name: string;
    slug: string;
  } | null;
  user_profiles: {
    display_name: string | null;
  } | null;
}

interface PostWithDetails {
  id: string;
  content: string;
  created_at: string;
  user_profiles: {
    display_name: string | null;
  } | null;
  forum_post_likes: Array<{
    user_id: string;
  }>;
}

export default function ForumTopic() {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [replyContent, setReplyContent] = useState("");

  const { data: topic } = useQuery({
    queryKey: ["forum-topic", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_topics")
        .select(`
          *,
          forum_categories (name, slug),
          user_profiles!forum_topics_user_id_fkey (display_name)
        `)
        .eq("id", id)
        .single();
      
      if (error) throw error;
      
      // Increment view count
      if (user) {
        await supabase.rpc("increment_topic_views", { _topic_id: id });
      }
      
      return data as TopicWithDetails;
    },
  });

  // SEO Configuration with dynamic content
  useSEO({
    title: topic 
      ? `${topic.title} - ChessHatch Community Discussion`
      : "Chess Forum Discussion - ChessHatch Community",
    description: topic
      ? `Join the discussion about "${topic.title}" in the ChessHatch chess community. ${topic.posts_count} replies and ${topic.views_count} views from chess enthusiasts worldwide.`
      : "Participate in chess discussions and connect with players from around the world in ChessHatch community forums.",
    keywords: `chess discussion, chess forum topic, chess community, chess strategy discussion, chess learning community`
  });

  const { data: posts, isLoading } = useQuery({
    queryKey: ["forum-posts", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_posts")
        .select(`
          *,
          user_profiles!forum_posts_user_id_fkey (display_name),
          forum_post_likes (user_id)
        `)
        .eq("topic_id", id)
        .order("created_at");
      
      if (error) throw error;
      return data as PostWithDetails[];
    },
    enabled: !!id,
  });

  const { data: isFollowing } = useQuery({
    queryKey: ["topic-following", id, user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase
        .from("forum_topic_follows")
        .select("id")
        .eq("topic_id", id)
        .eq("user_id", user.id)
        .single();
      
      return !error && !!data;
    },
    enabled: !!user?.id && !!id,
  });

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("forum_posts")
        .insert({
          topic_id: id,
          user_id: user.id,
          content,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setReplyContent("");
      queryClient.invalidateQueries({ queryKey: ["forum-posts", id] });
      queryClient.invalidateQueries({ queryKey: ["forum-topic", id] });
      toast({
        title: "Reply posted",
        description: "Your reply has been posted successfully.",
      });
    },
  });

  const toggleFollowMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      
      if (isFollowing) {
        const { error } = await supabase
          .from("forum_topic_follows")
          .delete()
          .eq("topic_id", id)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("forum_topic_follows")
          .insert({
            topic_id: id,
            user_id: user.id,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["topic-following", id, user?.id] });
      toast({
        title: isFollowing ? "Unfollowed topic" : "Following topic",
        description: isFollowing 
          ? "You will no longer receive notifications for this topic."
          : "You will receive notifications for new posts in this topic.",
      });
    },
  });

  const toggleLikeMutation = useMutation({
    mutationFn: async (postId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const existingLike = await supabase
        .from("forum_post_likes")
        .select("id")
        .eq("post_id", postId)
        .eq("user_id", user.id)
        .single();
      
      if (existingLike.data) {
        const { error } = await supabase
          .from("forum_post_likes")
          .delete()
          .eq("post_id", postId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("forum_post_likes")
          .insert({
            post_id: postId,
            user_id: user.id,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-posts", id] });
    },
  });

  if (!topic) {
    return (
      <ChessAppLayout>
        <div className="p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Topic not found</h1>
            <Button asChild>
              <Link to="/forums">Back to Forums</Link>
            </Button>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav className="text-sm text-muted-foreground mb-4">
          <Link to="/forums" className="hover:text-primary">Forums</Link>
          <span className="mx-2">›</span>
          <Link 
            to={`/forums/c/${topic.forum_categories?.slug}`}
            className="hover:text-primary"
          >
            {topic.forum_categories?.name}
          </Link>
          <span className="mx-2">›</span>
          <span>{topic.title}</span>
        </nav>

        {/* Topic Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {topic.is_pinned && <Pin className="w-5 h-5 text-primary" />}
                  {topic.is_locked && <Lock className="w-5 h-5 text-muted-foreground" />}
                  <CardTitle className="text-2xl">{topic.title}</CardTitle>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>by {topic.user_profiles?.display_name || "Unknown"}</span>
                  <span>{formatDistanceToNow(new Date(topic.created_at), { addSuffix: true })}</span>
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    <span>{topic.posts_count} posts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    <span>{topic.views_count} views</span>
                  </div>
                </div>
              </div>
              
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleFollowMutation.mutate()}
                  disabled={toggleFollowMutation.isPending}
                >
                  {isFollowing ? (
                    <>
                      <BellOff className="w-4 h-4 mr-2" />
                      Unfollow
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Follow
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Posts */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded w-full"></div>
                      <div className="h-3 bg-muted rounded w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            posts?.map((post, index) => {
              const userLiked = post.forum_post_likes?.some(like => like.user_id === user?.id);
              const likeCount = post.forum_post_likes?.length || 0;
              
              return (
                <Card key={post.id}>
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center gap-2">
                        <Avatar>
                          <AvatarFallback>
                            {post.user_profiles?.display_name?.charAt(0).toUpperCase() || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-xs text-center">
                          <div className="font-medium">{post.user_profiles?.display_name || "Unknown"}</div>
                          {index === 0 && <Badge variant="secondary" className="mt-1">OP</Badge>}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="mb-4">
                          <div className="text-sm text-muted-foreground mb-2">
                            Posted {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                          </div>
                          <div className="prose prose-sm max-w-none">
                            <p className="whitespace-pre-wrap">{post.content}</p>
                          </div>
                        </div>
                        
                        {user && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleLikeMutation.mutate(post.id)}
                              disabled={toggleLikeMutation.isPending}
                              className={userLiked ? "text-red-500" : ""}
                            >
                              <Heart className={`w-4 h-4 mr-1 ${userLiked ? "fill-current" : ""}`} />
                              {likeCount > 0 && likeCount}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Reply Form */}
        {user && !topic.is_locked && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Post a Reply</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Textarea
                  placeholder="Write your reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={4}
                />
                <Button
                  onClick={() => replyMutation.mutate(replyContent)}
                  disabled={!replyContent.trim() || replyMutation.isPending}
                >
                  {replyMutation.isPending ? "Posting..." : "Post Reply"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!user && (
          <Card className="mt-6">
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground mb-4">You must be logged in to reply to this topic.</p>
              <Button asChild>
                <Link to="/auth">Sign In</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </ChessAppLayout>
  );
}
